/**
 * @file PrivacyNotification.jsx
 * @description Displays a notification bar regarding privacy compliance.
 *              Uses Bootstrap Buttons.
 */

import React, { useEffect, useState } from "react";
import Button from "react-bootstrap/Button";
import { localData } from "../functions/api/localData";
import { Link } from "react-router-dom";

// Removed PolicyModal component definition and its propTypes

/**
 * @function PrivacyNotification
 * @description Handles the privacy notification bar. Prompts users to accept/reject privacy compliance.
 *              If not previously set, the notification is shown.
 * @returns {JSX.Element} The privacy notification bar.
 */
const PrivacyNotification = () => {
  const [showNotification, setShowNotification] = useState(false);

  // Removed state for controlling the policy modals

  useEffect(() => {
    /**
     * @function checkPrivacyStatus
     * @description Checks whether the privacy key is stored in localData. If not, show the notification.
     */
    const checkPrivacyStatus = async () => {
      const privacyStatus = await localData("get", "privacy");
      // Show notification only if no status is set (neither 'accept' nor 'reject')
      setShowNotification(!privacyStatus);
    };

    // Check privacy status on mount
    checkPrivacyStatus();

    // Removed polling interval
  }, []); // Dependency array is empty, runs only once on mount

  /**
   * @function handleAcceptPrivacy
   * @description Accepts privacy terms and saves the status in local storage.
   */
  const handleAcceptPrivacy = async () => {
    await localData("save", "privacy", "accept");
    setShowNotification(false);
  };

  /**
   * @function handleRejectPrivacy
   * @description Rejects privacy terms and saves the status in local storage.
   */
  const handleRejectPrivacy = async () => {
    await localData("save", "privacy", "reject");
    setShowNotification(false);
  };

  // Inline styles using CSS variables
  const styles = {
    notificationContainer: {
      position: "fixed",
      bottom: 0,
      width: "100%",
      backgroundColor: "var(--tertiary)", // Use theme tertiary color
      color: "var(--text-color)", // Use theme text color
      padding: "15px",
      textAlign: "center",
      zIndex: 1000,
      boxShadow: "0 -2px 5px rgba(0,0,0,0.1)",
      borderTop: '1px solid var(--border-color)', // Use theme border
    },
    notificationContent: {
      maxWidth: "800px",
      margin: "0 auto",
      display: "flex",
      flexDirection: "column", // Changed to column for better mobile layout
      alignItems: "center",
      gap: '10px', // Add gap between message and buttons
    },
    message: {
      fontSize: "0.95rem",
      lineHeight: 1.6,
      marginBottom: '10px',
    },
    linkStyle: { // Style for the Link component
      color: 'var(--link-color)', // Use theme link color
      textDecoration: 'underline',
      fontWeight: 'bold',
      cursor: 'pointer',
    },
    ButtonContainer: {
      display: "flex",
      gap: "10px",
      flexWrap: 'wrap', // Allow buttons to wrap on smaller screens
      justifyContent: 'center', // Center buttons when wrapped
    },
    acceptButton: {
      fontWeight: "bold",
    },
    rejectButton: {
      fontWeight: "bold",
    },
  };


  return (
    <React.Fragment>
      {showNotification && (
        <div style={styles.notificationContainer}>
          <div style={styles.notificationContent}>
            <p style={styles.message}>
              We use cookies and similar technologies to enhance your experience and for analytics. By continuing to use our site, you agree to our practices. Please review our{" "}
              <Link to={"/privacy/"} style={styles.linkStyle}>
                Privacy Policy
              </Link>
              {" "}for more details. You can manage your preferences below.
            </p>

            <div style={styles.ButtonContainer}>
              <Button
                variant="primary" // Use variant
                onClick={handleAcceptPrivacy}
                style={styles.acceptButton}
              >
                Accept
              </Button>
              <Button
                variant="secondary"
                onClick={handleRejectPrivacy}
                style={styles.rejectButton}
              >
                Reject
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Removed Modal rendering */}
    </React.Fragment>
  );
};

export default PrivacyNotification;
